package org.teddapp.project.ui.review

import androidx.compose.runtime.Composable
import androidx.compose.runtime.collectAsState
import androidx.compose.runtime.getValue
import androidx.compose.runtime.mutableStateOf
import androidx.compose.runtime.remember
import androidx.compose.runtime.rememberCoroutineScope
import androidx.compose.runtime.setValue
import com.tedd.db.Flashcard
import com.tedd.db.Skill
import kotlinx.coroutines.CoroutineScope
import kotlinx.coroutines.launch
import org.teddapp.project.core.CoreSession
import org.teddapp.project.core.LocalCoreSession
import org.teddapp.project.db.Leitner
import org.teddapp.project.db.TeddRepositories
import org.teddapp.project.ui.TeddDates

/**
 * État des révisions. Une carte est due quand sa date est arrivée ; on la retourne,
 * on dit si on l'a su, et la boîte de Leitner décide de la prochaine date.
 */
internal class ReviewModel(
    private val repos: TeddRepositories,
    private val scope: CoroutineScope,
    private val core: CoreSession? = null,
) {
    /**
     * Jours d'attente par case, annoncés par le noyau. Vide sans lui : l'écran dit
     * alors qu'il ne peut pas planifier, au lieu de recalculer une règle qui ne lui
     * appartient plus.
     */
    val intervals: List<Int> = core?.bridge?.leitnerIntervals().orEmpty()

    /** Nombre de cases, déduit de ce que le noyau annonce. */
    val boxCount: Int get() = intervals.size
    var cards by mutableStateOf<List<Flashcard>>(emptyList())
    var skills by mutableStateOf<List<Skill>>(emptyList())

    /** Compétence sur laquelle réviser, ou null pour tout. */
    var skillFilter by mutableStateOf<String?>(null)
    var revealed by mutableStateOf(false)
    var creating by mutableStateOf(false)
    var editing by mutableStateOf<Flashcard?>(null)
    /** Cartes déjà vues dans cette session, pour ne pas les repasser en boucle. */
    var seen by mutableStateOf<Set<String>>(emptySet())
    var lastResult by mutableStateOf<String?>(null)

    private val live: List<Flashcard> get() = cards.filter { it.is_deleted == 0L }

    val filtered: List<Flashcard>
        get() = live.filter { skillFilter == null || it.skill_id == skillFilter }

    /** Cartes à réviser aujourd'hui, les plus en retard d'abord. */
    val due: List<Flashcard>
        get() = filtered.filter { it.due_date <= TeddDates.todayIso }.sortedWith(compareBy({ it.due_date }, { it.order_index }))

    val remaining: List<Flashcard> get() = due.filter { it.id !in seen }
    val current: Flashcard? get() = remaining.firstOrNull()

    val dueCount: Int get() = due.size
    val doneToday: Int get() = seen.size
    val totalCards: Int get() = filtered.size

    /** Répartition dans les six cases, pour voir ce qui est acquis et ce qui résiste. */
    val boxes: List<Int>
        get() = (1..boxCount).map { b -> filtered.count { it.box.toInt() == b } }

    fun skillName(id: String?): String? = skills.firstOrNull { it.id == id }?.name

    fun reveal() { revealed = true }

    fun answer(remembered: Boolean) {
        val card = current ?: return
        lastResult = null
        scope.launch {
            val etat = repos.flashcards.review(card, remembered, core)
            lastResult = when {
                etat == null -> "Noté, mais sans noyau la prochaine date reste inconnue."
                remembered -> "Su · ${Leitner.label(etat.intervalleJours)}"
                else -> "À revoir · ${Leitner.label(etat.intervalleJours)}"
            }
        }
        seen = seen + card.id
        revealed = false
    }

    fun restartSession() { seen = emptySet(); revealed = false; lastResult = null }

    fun save(existing: Flashcard?, front: String, back: String, skillId: String?, note: String?) {
        scope.launch {
            if (existing == null) {
                repos.flashcards.create(front = front, back = back, skillId = skillId, note = note, orderIndex = cards.size.toLong())
            } else {
                repos.flashcards.update(existing.id, skillId, front, back, note)
            }
        }
        creating = false
        editing = null
    }

    fun delete(card: Flashcard) {
        scope.launch { repos.flashcards.softDelete(card.id) }
        editing = null
    }
}

@Composable
internal fun rememberReviewModel(repos: TeddRepositories): ReviewModel {
    val scope = rememberCoroutineScope()
    val core = LocalCoreSession.current
    val model = remember(repos, core) { ReviewModel(repos, scope, core) }
    model.cards = repos.flashcards.observeAll().collectAsState(emptyList()).value
    model.skills = repos.skills.observeAll().collectAsState(emptyList()).value
    return model
}
